'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getWebAppOrigin } from '@/lib/web-app-origin';
import { Link } from '@tanstack/react-router';
import { Check, ExternalLink, Rocket } from 'lucide-react';
import {
  useCheckSetupAgain,
  useSetupNextStep,
} from '../api/get-setup-next-step';

/**
 * The path inside this app, when `url` is one of ours; null for anything
 * else (Meta). Compared against the app's public origin, not the page's: in
 * the native app the page is `capacitor://localhost`, and an app link must
 * still stay in the app rather than open a signed-out browser.
 */
export function appPath(url: string): string | null {
  try {
    const target = new URL(url);
    if (target.origin !== new URL(getWebAppOrigin()).origin) return null;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}

/**
 * "Next step for your ads" on the home screen: the one setup step standing
 * between a paying clinic and its first ad, with a button straight to the
 * fix. Shown only while it applies (paying a few days, no ad spend yet, and
 * a step the owner can do), so it goes away by itself once the step is done.
 */
export function AdsSetupNextStepCard() {
  const { nextStep } = useSetupNextStep();
  const { checkAgain, isChecking } = useCheckSetupAgain();
  if (!nextStep) return null;
  const inApp = nextStep.actionUrl ? appPath(nextStep.actionUrl) : null;

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="ads-setup-next-step"
      data-testid="ads-setup-card"
    >
      <p className="text-sm text-muted-foreground">
        Next step for your ads · step {nextStep.stepNumber} of{' '}
        {nextStep.totalSteps}
      </p>
      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-xs">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
          >
            <Rocket className="size-4" />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h2
              id="ads-setup-next-step"
              className="text-base font-semibold text-foreground"
            >
              {nextStep.title}
            </h2>
            <p className="text-sm text-muted-foreground">{nextStep.body}</p>
          </div>
        </div>
        <ol className="flex gap-1" aria-label="Ads setup progress">
          {nextStep.steps.map((s) => (
            <li
              key={s.step}
              aria-label={`${s.label}: ${s.done ? 'done' : s.step === nextStep.step ? 'next' : 'to do'}`}
              className={cn(
                'h-1.5 flex-1 rounded-full',
                s.done
                  ? 'bg-primary'
                  : s.step === nextStep.step
                    ? 'bg-primary/40'
                    : 'bg-muted'
              )}
            />
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          {inApp ? (
            <Button asChild size="sm">
              <Link to={inApp as never}>{nextStep.actionLabel}</Link>
            </Button>
          ) : nextStep.actionUrl ? (
            <Button asChild size="sm">
              <a href={nextStep.actionUrl} target="_blank" rel="noreferrer">
                {nextStep.actionLabel}
                <ExternalLink />
              </a>
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={isChecking}
            onClick={() => checkAgain()}
          >
            <Check />
            {isChecking ? 'Checking…' : "I've done this"}
          </Button>
        </div>
      </div>
    </section>
  );
}
